import { authStorageKeys } from '@/constants/storage'
import AuthAPI from '@/features/api/auth/auth'
import { AuthAPIErrorCodeEnum } from '@/features/api/auth/constants'
import LicenceAPI from '@/features/api/licence/licence'
import { MasterAPIErrorCodeEnum } from '@/features/api/master/constants'
import MasterAPI from '@/features/api/master/master'
import { OAuthErrorTypeEnum } from '@/features/api/oauth/constants'
import OAuthAPI from '@/features/api/oauth/oauth'
import { setAllApisHostUrl } from '@/features/api/setAllApisHostUrl'
import { isExpectedApiError, isNetworkConnectionError } from '@/features/api/utils'
import { InitialAuthErrorCode } from '@/features/auth/constants/errors'
import { logger } from '@/features/auth/logger'
import { oauthAccessToken } from '@/features/auth/oauthAccessToken'
import { InitialAuthThunkRejectExtra } from '@/features/auth/types'
import { getDeviceInfo } from '@/features/auth/utils/getDeviceInfo'
import { logoutCurrentProfile, logoutDevice } from '@/redux/reducers/auth/actions'
import { AuthSessionData, AuthState } from '@/redux/reducers/auth/types'
import { RootState } from '@/redux/store'
import { ThunkRejectValue } from '@/redux/types'
import { getThunkRejectValue } from '@/redux/utils'
import StorageService from '@/services/storage'
import { IProfile } from '@/types/profile'
import { asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit'
import invariant from 'tiny-invariant'

const initialState: AuthState = {
    licenceId: '',
    licenceName: null,
    deviceId: null,
    profiles: null,
    currentProfileUuid: null,
    completed: false,
}

export const createAuthSlice = buildCreateSlice({
    creators: { asyncThunk: asyncThunkCreator },
})

const authSlice = createAuthSlice({
    name: 'auth',
    initialState,
    reducers: (create) => {
        const createThunk = create.asyncThunk.withTypes<{
            rejectValue: ThunkRejectValue
        }>()

        return {
            setAuthLicenceId: create.reducer<string>(
                (state, { payload: licenceId }) => void (state.licenceId = licenceId)
            ),
            lookupAuthLicence: createThunk<string>(
                async (_, { getState, rejectWithValue }) => {
                    try {
                        const { licenceId } = (getState() as RootState).auth

                        const licence = await MasterAPI.licenceLookup(licenceId)

                        await StorageService.setLicenceId(licenceId)

                        setAllApisHostUrl(licence.url)

                        return licence.name
                    } catch (e) {
                        const expectedApiErrorIdentifiers = [
                            MasterAPIErrorCodeEnum.EntityNotFound.toString(),
                        ]

                        if (!isExpectedApiError(e, expectedApiErrorIdentifiers)) logger.errorOnly(e)

                        return rejectWithValue(getThunkRejectValue(e, expectedApiErrorIdentifiers))
                    }
                },
                {
                    fulfilled: (state, { payload: licenceName }) =>
                        void (state.licenceName = licenceName),
                }
            ),
            fetchAuthDeviceId: createThunk<string>(
                async (_, { rejectWithValue }) => {
                    try {
                        const deviceId = await AuthAPI.requestDeviceAuthorization(getDeviceInfo())

                        await StorageService.setDeviceId(deviceId)

                        return deviceId
                    } catch (e) {
                        await StorageService.removeLicenceId()

                        logger.errorOnly(e)

                        return rejectWithValue(getThunkRejectValue(e))
                    }
                },
                {
                    fulfilled: (state, { payload: deviceId }) => void (state.deviceId = deviceId),
                    rejected: () => initialState,
                }
            ),
            fetchAuthDeviceToken: createThunk<void, string>(
                async (deviceCode, { getState, rejectWithValue }) => {
                    try {
                        const { deviceId } = (getState() as RootState).auth

                        invariant(deviceId, "'deviceId' must be set in redux")

                        const deviceToken = await AuthAPI.authorizeDevice(deviceId, deviceCode)

                        await StorageService.setDeviceToken(deviceToken)

                        LicenceAPI.setAccessToken(deviceToken)
                    } catch (e) {
                        const expectedApiErrorIdentifiers = [
                            AuthAPIErrorCodeEnum.NotGranted.toString(),
                        ]

                        if (!isExpectedApiError(e, expectedApiErrorIdentifiers)) logger.errorOnly(e)

                        return rejectWithValue(getThunkRejectValue(e, expectedApiErrorIdentifiers))
                    }
                }
            ),
            clearAuthLicenceNameAndDeviceId: createThunk(
                async () => {
                    try {
                        await StorageService.removeDeviceId()
                    } catch (e) {
                        logger.errorOnly(e)
                    }
                },
                {
                    fulfilled: (state) => {
                        state.licenceName = initialState.licenceName
                        state.deviceId = initialState.deviceId
                    },
                }
            ),
            clearAuth: createThunk(
                async () => {
                    try {
                        await StorageService.removeByKeys(authStorageKeys)
                    } catch (e) {
                        logger.errorOnly(e)
                    }
                },
                {
                    fulfilled: () => initialState,
                }
            ),
            fetchAuthProfiles: createThunk<IProfile[]>(
                async (_, { rejectWithValue }) => {
                    try {
                        return await LicenceAPI.fetchProfiles()
                    } catch (e) {
                        logger.errorOnly(e)

                        return rejectWithValue(getThunkRejectValue(e))
                    }
                },
                {
                    fulfilled: (state, { payload: profiles }) => void (state.profiles = profiles),
                }
            ),
            setCurrentAuthProfile: createThunk<string, string>(
                async (uuid, { rejectWithValue }) => {
                    try {
                        await StorageService.setCurrentProfileUuid(uuid)

                        return uuid
                    } catch (e) {
                        logger.errorOnly(e)

                        return rejectWithValue(getThunkRejectValue(e))
                    }
                },
                {
                    fulfilled: (state, { payload: currentProfileUuid }) =>
                        void (state.currentProfileUuid = currentProfileUuid),
                }
            ),
            clearCurrentAuthProfile: createThunk(
                async () => {
                    try {
                        await StorageService.removeCurrentProfileUuid()
                    } catch (e) {
                        logger.errorOnly(e)
                    }
                },
                {
                    fulfilled: (state) =>
                        void (state.currentProfileUuid = initialState.currentProfileUuid),
                }
            ),
            fetchAuthTokens: createThunk<void, string>(
                async (pin, { getState, rejectWithValue }) => {
                    try {
                        const { deviceId, currentProfileUuid } = (getState() as RootState).auth

                        invariant(
                            deviceId && currentProfileUuid,
                            "'deviceId' and 'currentProfileUuid' must be set in redux"
                        )

                        const deviceToken = await StorageService.getDeviceToken()

                        invariant(deviceToken, "'deviceToken' must be set in storage")

                        const tokens = await OAuthAPI.fetchTokens(
                            deviceId,
                            deviceToken,
                            currentProfileUuid,
                            pin
                        )

                        oauthAccessToken.set(tokens.accessToken)

                        await StorageService.setRefreshToken(tokens.refreshToken)
                    } catch (e) {
                        const expectedApiErrorIdentifiers = [
                            OAuthErrorTypeEnum.InvalidClient,
                            OAuthErrorTypeEnum.InvalidGrant,
                        ]

                        if (!isExpectedApiError(e, expectedApiErrorIdentifiers)) {
                            logger.errorOnly(e)
                        } else {
                            if (e.identifier === OAuthErrorTypeEnum.InvalidClient)
                                await StorageService.removeByKeys(authStorageKeys)
                        }

                        return rejectWithValue(getThunkRejectValue(e, expectedApiErrorIdentifiers))
                    }
                },
                {
                    fulfilled: (state) => void (state.completed = true),
                    rejected: (_, { payload }) => {
                        if (
                            payload?.expectedApiError?.identifier ===
                            OAuthErrorTypeEnum.InvalidClient
                        ) {
                            return initialState
                        }
                    },
                }
            ),
            setInitialAuth: createThunk<
                AuthSessionData | null,
                void,
                {
                    rejectValue: ThunkRejectValue<InitialAuthThunkRejectExtra>
                }
            >(
                async (_, { rejectWithValue }) => {
                    try {
                        const licenceId = await StorageService.getLicenceId()

                        if (!licenceId) return null

                        const licence = await MasterAPI.licenceLookup(licenceId)

                        setAllApisHostUrl(licence.url)

                        const deviceToken = await StorageService.getDeviceToken()

                        if (!deviceToken) return null

                        LicenceAPI.setAccessToken(deviceToken)

                        const deviceId = await StorageService.getDeviceId()
                        const profiles = await LicenceAPI.fetchProfiles()
                        const currentProfileUuid = await StorageService.getCurrentProfileUuid()

                        const currentProfileExists = profiles?.some(
                            ({ uuid }) => uuid === currentProfileUuid
                        )

                        if (!currentProfileExists) {
                            await StorageService.removeCurrentProfileUuid()
                        }

                        return {
                            licenceId,
                            licenceName: licence.name,
                            deviceId,
                            profiles,
                            currentProfileUuid: currentProfileExists ? currentProfileUuid : null,
                        }
                    } catch (e) {
                        const expectedApiErrorIdentifiers = [
                            MasterAPIErrorCodeEnum.EntityNotFound.toString(),
                        ]

                        if (isExpectedApiError(e, expectedApiErrorIdentifiers)) {
                            await StorageService.removeByKeys(authStorageKeys)

                            return null
                        }

                        let extra

                        if (isNetworkConnectionError(e)) {
                            extra = { code: InitialAuthErrorCode.NoInternet }
                        } else {
                            extra = { code: InitialAuthErrorCode.Unknown }
                            logger.errorOnly(e)
                        }

                        return rejectWithValue(getThunkRejectValue(e, undefined, extra))
                    }
                },
                {
                    fulfilled: (_, { payload: authSessionData }) => {
                        if (!authSessionData) return

                        return { ...authSessionData, completed: initialState.completed }
                    },
                }
            ),
        }
    },
    extraReducers: (builder) => {
        builder.addCase(logoutDevice.fulfilled, () => initialState)
        builder.addCase(logoutCurrentProfile.fulfilled, (state) => {
            state.currentProfileUuid = initialState.currentProfileUuid
            state.completed = initialState.completed
        })
    },
})

export const {
    setAuthLicenceId,
    lookupAuthLicence,
    fetchAuthDeviceId,
    fetchAuthDeviceToken,
    clearAuthLicenceNameAndDeviceId,
    clearAuth,
    fetchAuthProfiles,
    setCurrentAuthProfile,
    clearCurrentAuthProfile,
    fetchAuthTokens,
    setInitialAuth,
} = authSlice.actions

export default authSlice.reducer
